/**
 * Signatures — what a signature on this repository IS, and what applies it.
 *
 * The tab exists because the choice it presents is one somebody has to make
 * deliberately and, until now, could not make at all. Every document in the
 * platform was signed as an **audit-trail record**: a real 21 CFR Part 11
 * electronic signature — identity, meaning, time, two verified identification
 * components, an insert-only trail and a human-readable manifestation — and
 * not a signature a third party can check without asking this server. The
 * drivers for the other kind were written, tested and never reachable, because
 * there was nowhere to register a certificate.
 *
 * Two things this screen must never do, and both are structural here rather
 * than left to care:
 *
 * **It must not imply cryptography that was not applied.** The regime a
 * document type declares is what decides, and the summary at the top says in
 * words which of the two this repository produces today.
 *
 * **It must not present an audit-trail signature as a degraded one.** It is
 * what most regulated QMS products ship and it satisfies Part 11 in full. The
 * reason to add a certificate is that a third party — a regulator, a customer,
 * a court — can verify the file on their own, and that is the sentence used.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  btnStyle as dmsBtnStyle, cardStyle as dmsCardStyle,
  inputStyle as dmsInputStyle, labelStyle as dmsLabelStyle,
  RADIUS, T, TYPE,
} from '@twasta/page-runtime/dms'
import type { DmsSigningCredential } from '@twasta/page-runtime/dms'

import {
  deactivateSigningCredential, listSigningCredentials,
  registerSigningCredential, signingCredentialHealth,
} from '@/api/dms'

/**
 * The shipped regimes, and what each one actually produces.
 *
 * Mirrors `dms/signing.PROFILES`, which is the authority — this is the English
 * for it. `part_11` sitting at "no cryptography" is not an omission and is
 * labelled so nobody reads it as one: Part 11 does not require PKI, and saying
 * it does is how a project ends up buying an HSM it does not need.
 */
const PROFILES: {
  key: string; label: string; crypto: boolean; note: string
}[] = [
  { key: 'general', label: 'General business', crypto: false,
    note: 'An audit-trail record. No certificate needed.' },
  { key: 'part_11', label: '21 CFR Part 11 / ISO 9001', crypto: false,
    note: 'An audit-trail record, with a password re-entered for every '
      + 'signature. Part 11 does not require a certificate — this satisfies '
      + 'it in full.' },
  { key: 'in_it_act', label: 'India IT Act (DSC / Aadhaar eSign)', crypto: true,
    note: 'A cryptographic signature with a timestamp from the issuing CA’s '
      + 'own authority, and revocation information embedded.' },
  { key: 'eidas_aes', label: 'eIDAS Advanced (AES)', crypto: true,
    note: 'A cryptographic signature. A software key in a database cannot '
      + 'back this claim — it needs a hardware token, an HSM or a KMS.' },
  { key: 'eidas_qes', label: 'eIDAS Qualified (QES)', crypto: true,
    note: 'A qualified signature, applied by a licensed provider under the '
      + 'signer’s sole control. Not something this platform applies itself.' },
]

export function SignaturesTab({ project, objName, repository, patch }: {
  project: string
  objName: string
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
}) {
  const [credentials, setCredentials] = useState<DmsSigningCredential[]>([])
  const [available, setAvailable] =
    useState<{ ok: boolean; detail: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const doctypes: any[] = repository.document_types || []

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const res = await listSigningCredentials(project, objName)
      setCredentials(res.credentials || [])
      setAvailable(res.pki_available || null)
    } catch (err: any) {
      // A repository whose signature tables have not been provisioned answers
      // with an empty list, not an error — so anything that reaches here is a
      // real failure and is SHOWN. An empty rail that silently means "could
      // not ask" would read as "no certificates registered", which is a
      // different and much more comfortable thing to believe.
      setLoadError(err?.response?.data?.detail
        || 'The signing credentials for this repository could not be read.')
    } finally {
      setLoading(false)
    }
  }, [project, objName])

  useEffect(() => { void load() }, [load])

  /** Which document types ask for cryptography, by their declared regime. */
  const cryptoTypes = useMemo(() => doctypes.filter((t) => {
    const profile = String(t?.signatures?.profile || 'general')
    return PROFILES.find((p) => p.key === profile)?.crypto
  }), [doctypes])

  const signingTypes = useMemo(
    () => doctypes.filter((t) => t?.signatures?.enabled), [doctypes])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Summary signingTypes={signingTypes} cryptoTypes={cryptoTypes}
               credentials={credentials} available={available} />

      <section style={panelStyle}>
        <h3 style={groupTitleStyle}>What each kind of document asks for</h3>
        <p style={helpStyle}>
          A regime is declared on the document TYPE, beside its slots, because
          a repository routinely holds both — an internal note signed off by
          one person, and an agreement two parties execute.
        </p>
        {doctypes.length === 0 ? (
          <p style={helpStyle}>
            This repository has no document types yet. Add one under
            <strong> Kinds of document</strong> first.
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Document type</th>
                <th style={thStyle}>Signatures</th>
                <th style={thStyle}>Regime</th>
                <th style={thStyle}>Applied by</th>
              </tr>
            </thead>
            <tbody>
              {doctypes.map((type, index) => (
                <TypeRow key={type.key || index} type={type} index={index}
                         patch={patch} credentials={credentials} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <CredentialsPanel
        project={project} objName={objName}
        credentials={credentials} available={available}
        loading={loading} loadError={loadError} onChanged={load}
        needed={cryptoTypes.length > 0}
      />

      <TimestampPanel repository={repository} patch={patch}
                      needed={cryptoTypes.some((t) => {
                        const key = String(t?.signatures?.profile || '')
                        return key === 'in_it_act' || key === 'eidas_aes'
                          || key === 'eidas_qes'
                      })} />
    </div>
  )
}

// ── the headline ───────────────────────────────────────────────────────────

/**
 * What this repository produces TODAY, in one sentence.
 *
 * Said first and said plainly, because the failure this whole feature has to
 * avoid is a reader believing a document carries more than it does. The three
 * states are: nothing signs here; signatures are audit-trail records; a
 * certificate is configured and cryptography is applied.
 */
function Summary({ signingTypes, cryptoTypes, credentials, available }: {
  signingTypes: any[]
  cryptoTypes: any[]
  credentials: DmsSigningCredential[]
  available: { ok: boolean; detail: string } | null
}) {
  const usable = credentials.filter((c) => c.active && !c.expired)

  if (signingTypes.length === 0) {
    return (
      <div style={noteStyle}>
        No document type in this repository asks for a signature yet. Turn
        signatures on for a type under <strong>Kinds of document</strong>, then
        come back here to choose how they are applied.
      </div>
    )
  }

  if (cryptoTypes.length === 0) {
    return (
      <div style={noteStyle}>
        <strong>Signatures here are audit-trail records.</strong> Who signed,
        when, and what they meant is recorded in an insert-only log and shown
        on the document’s manifest page — a complete 21 CFR Part 11 electronic
        signature. What it is not is a signature somebody outside this system
        can check on their own. Choose a regime below that asks for a
        certificate if the documents leave the organisation.
      </div>
    )
  }

  if (!available?.ok) {
    return (
      <div style={warnStyle}>
        <strong>{cryptoTypes.length === 1 ? 'A document type asks'
          : `${cryptoTypes.length} document types ask`} for a cryptographic
        signature, and this server cannot apply one.</strong>
        <div style={{ marginTop: 4 }}>{available?.detail || ''}</div>
        <div style={{ marginTop: 4 }}>
          Until it can, those documents can still be signed as audit-trail
          records — nothing is blocked, and nothing claims a certificate that
          did not sign.
        </div>
      </div>
    )
  }

  if (usable.length === 0) {
    return (
      <div style={warnStyle}>
        <strong>{cryptoTypes.length === 1 ? 'A document type asks'
          : `${cryptoTypes.length} document types ask`} for a cryptographic
        signature, and no usable certificate is registered.</strong>
        <div style={{ marginTop: 4 }}>
          Signing one of those documents will be refused, with that reason,
          rather than quietly recorded as an audit-trail signature. Register a
          certificate below.
        </div>
      </div>
    )
  }

  return (
    <div style={okStyle}>
      <strong>Cryptographic signatures are configured.</strong> Documents of
      {cryptoTypes.length === 1 ? ' one type ' : ` ${cryptoTypes.length} types `}
      are signed with {usable.length === 1 ? 'the certificate' : 'a certificate'}
      {' '}below, and the signature is embedded in the PDF — anybody can open
      the file in a PDF reader and verify it without asking this system
      anything. The audit-trail record is kept as well; the two are not
      alternatives.
    </div>
  )
}

// ── per document type ──────────────────────────────────────────────────────

function TypeRow({ type, index, patch, credentials }: {
  type: any
  index: number
  patch: (path: string[], value: unknown) => void
  credentials: DmsSigningCredential[]
}) {
  const signatures = type.signatures || {}
  const enabled = Boolean(signatures.enabled)
  const profileKey = String(signatures.profile || 'general')
  const profile = PROFILES.find((p) => p.key === profileKey)
  const slots: any[] = signatures.slots || []

  const set = (key: string, value: unknown) =>
    patch(['repository', 'document_types', String(index), 'signatures', key],
          value)

  const usable = credentials.filter((c) => c.active && !c.expired)

  return (
    <>
      <tr>
        <td style={tdStyle}>
          <div style={{ fontWeight: 600 }}>{type.title || type.key}</div>
          <div style={{ ...helpStyle, margin: 0 }}>
            {slots.length === 0 ? 'No slots declared'
              : `${slots.length} signature${slots.length === 1 ? '' : 's'}`}
          </div>
        </td>
        <td style={tdStyle}>
          <label style={checkLabelStyle}>
            <input type="checkbox" checked={enabled}
                   onChange={(e) => set('enabled', e.target.checked)} />
            {enabled ? 'Required' : 'Off'}
          </label>
        </td>
        <td style={tdStyle}>
          <select value={profileKey} disabled={!enabled} style={inputStyle}
                  onChange={(e) => set('profile', e.target.value)}>
            {PROFILES.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
          <div style={{ ...helpStyle, margin: '4px 0 0' }}>
            {profile?.note || ''}
          </div>
        </td>
        <td style={tdStyle}>
          {!profile?.crypto ? (
            <span style={{ fontSize: TYPE.small, color: T.textSecondary }}>
              The system’s own record
            </span>
          ) : (
            <>
              <select value={String(signatures.credential || '')}
                      disabled={!enabled} style={inputStyle}
                      onChange={(e) => set('credential', e.target.value)}>
                {/* Empty is legitimate and is NOT "none": a repository with
                    exactly one usable certificate resolves to it, so every
                    slot in every type would otherwise have to repeat the same
                    id. More than one and the server refuses rather than
                    guessing which key an organisation signs with. */}
                <option value="">
                  {usable.length === 1
                    ? `The registered certificate (${usable[0].label})`
                    : usable.length === 0
                      ? '— none registered —'
                      : '— choose a certificate —'}
                </option>
                {usable.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              {usable.length > 1 && !signatures.credential && (
                <div style={{ ...helpStyle, margin: '4px 0 0',
                              color: T.warning }}>
                  More than one certificate is registered, so this type must
                  name the one it uses.
                </div>
              )}
            </>
          )}
        </td>
      </tr>
      {enabled && slots.length > 0 && (
        <tr>
          <td colSpan={4} style={{ ...tdStyle, paddingTop: 0 }}>
            <SlotList slots={slots} typeIndex={index} patch={patch} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The slots, and the one thing this screen adds to them: the drawn mark.
 *
 * `ink` is declared HERE rather than offered at signing time for the reason
 * `_check_ink` states on the server: which slots carry a drawn mark has to be
 * a property of the document type, or it becomes a property of whatever the
 * client happened to send. A slot that asks for one and is given none is
 * refused — a signature line printed blank reads exactly like one nobody
 * signed.
 */
function SlotList({ slots, typeIndex, patch }: {
  slots: any[]
  typeIndex: number
  patch: (path: string[], value: unknown) => void
}) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {slots.map((slot, slotIndex) => (
        <div key={slot.key || slotIndex} style={slotRowStyle}>
          <span style={{ fontWeight: 600, minWidth: 120 }}>
            {slot.key || `Signature ${slotIndex + 1}`}
          </span>
          <span style={{ fontSize: TYPE.small, color: T.textSecondary,
                         minWidth: 120 }}>
            {slot.role ? `signed by ${slot.role}` : 'anyone who may sign'}
          </span>
          <span style={{ fontSize: TYPE.small, color: T.textSecondary,
                         minWidth: 100 }}>
            {slot.meaning || 'approval'}
          </span>
          <label style={checkLabelStyle}>
            <input
              type="checkbox"
              checked={Boolean(slot.ink)}
              onChange={(e) => patch(
                ['repository', 'document_types', String(typeIndex),
                 'signatures', 'slots', String(slotIndex), 'ink'],
                e.target.checked)}
            />
            Draw a signature
          </label>
        </div>
      ))}
      <p style={{ ...helpStyle, margin: 0 }}>
        A drawn mark appears on the document. It is a manifestation, never the
        authentication — what proves who signed is the credential the regime
        asks for, and the mark is what a reader recognises on the page.
      </p>
    </div>
  )
}

// ── certificates ───────────────────────────────────────────────────────────

function CredentialsPanel({
  project, objName, credentials, available, loading, loadError, onChanged,
  needed,
}: {
  project: string
  objName: string
  credentials: DmsSigningCredential[]
  available: { ok: boolean; detail: string } | null
  loading: boolean
  loadError: string
  onChanged: () => void | Promise<void>
  needed: boolean
}) {
  const [label, setLabel] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement | null>(null)

  const submit = async () => {
    if (!file || !label.trim()) return
    setBusy(true)
    setError('')
    try {
      const res = await registerSigningCredential(project, objName, {
        file, label: label.trim(), passphrase,
      })
      toast.success(`Registered ${res.subject_dn || label.trim()}`)
      setLabel('')
      setPassphrase('')
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      await onChanged()
    } catch (err: any) {
      // A wrong passphrase and an unreadable bundle come back as 422 with the
      // server's own wording, which distinguishes them. Shown verbatim: they
      // are two different things to do something about, and a single
      // "registration failed" would send somebody hunting the wrong one.
      setError(err?.response?.data?.detail
        || 'That certificate could not be registered.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={panelStyle}>
      <h3 style={groupTitleStyle}>Certificates</h3>
      <p style={helpStyle}>
        The organisation’s own certificate, used to seal documents on approval.
        Under eIDAS this produces a <strong>seal</strong> rather than a personal
        signature — the certificate belongs to the organisation, and the human
        approver is named in the appearance, the manifest and the audit trail.
        That is correct and defensible, and it must never be described as the
        approver’s own signature.
      </p>
      <p style={helpStyle}>
        The certificate file is sealed into the platform’s own key store, not
        into this application’s database, and is never returned by any screen
        or API after it is registered.
      </p>

      {available && !available.ok && (
        <div style={warnStyle}>{available.detail}</div>
      )}
      {loadError && <div style={warnStyle}>{loadError}</div>}

      {loading ? (
        <p style={helpStyle}>Loading…</p>
      ) : credentials.length === 0 ? (
        <p style={helpStyle}>
          {needed
            ? 'None registered. A document type here asks for a cryptographic '
              + 'signature, so one is needed before those documents can be '
              + 'signed.'
            : 'None registered, and none needed: no document type here asks '
              + 'for a cryptographic signature.'}
        </p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Label</th>
              <th style={thStyle}>Certificate</th>
              <th style={thStyle}>Valid until</th>
              <th style={thStyle}>State</th>
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential) => (
              <CredentialRow key={credential.id} credential={credential}
                             project={project} objName={objName}
                             onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 14, paddingTop: 12,
                    borderTop: `1px solid ${T.border}` }}>
        <h4 style={{ ...groupTitleStyle, fontSize: TYPE.body }}>
          Register a certificate
        </h4>
        <div style={{ display: 'grid', gap: 8,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <label style={labelStyle}>
            What to call it
            <input style={inputStyle} value={label}
                   placeholder="e.g. Acme Pharma corporate seal 2026"
                   onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label style={labelStyle}>
            PKCS#12 file (.p12 / .pfx)
            <input ref={fileRef} type="file" style={inputStyle}
                   accept=".p12,.pfx,application/x-pkcs12"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <label style={labelStyle}>
            Its passphrase
            <input style={inputStyle} type="password" value={passphrase}
                   autoComplete="new-password"
                   onChange={(e) => setPassphrase(e.target.value)} />
          </label>
        </div>
        <p style={helpStyle}>
          The subject, issuer, serial and validity are read out of the
          certificate rather than typed, so what this screen shows can never
          name one certificate while a different one signs.
        </p>
        {error && <div style={warnStyle}>{error}</div>}
        <button type="button" style={btnStyle} disabled={busy || !file
                  || !label.trim()} onClick={() => void submit()}>
          {busy ? 'Registering…' : 'Register'}
        </button>
      </div>
    </section>
  )
}

function CredentialRow({ credential, project, objName, onChanged }: {
  credential: DmsSigningCredential
  project: string
  objName: string
  onChanged: () => void | Promise<void>
}) {
  const [checking, setChecking] = useState(false)
  const [health, setHealth] =
    useState<{ ok: boolean; detail: string } | null>(null)

  const check = async () => {
    setChecking(true)
    try {
      const res = await signingCredentialHealth(project, objName, credential.id)
      setHealth({ ok: Boolean(res.ok), detail: res.detail || '' })
    } catch (err: any) {
      setHealth({ ok: false, detail: err?.response?.data?.detail
        || 'That certificate could not be checked.' })
    } finally {
      setChecking(false)
    }
  }

  const withdraw = async () => {
    // Withdrawing keeps the row. A withdrawn certificate must stay nameable by
    // the signatures it already produced — "which key signed this, and was it
    // valid then" is the first question asked about a disputed signature — so
    // this is not a delete and the wording does not pretend it is.
    try {
      await deactivateSigningCredential(project, objName, credential.id)
      toast.success(`${credential.label} withdrawn`)
      await onChanged()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail
        || 'That certificate could not be withdrawn.')
    }
  }

  const state = !credential.active ? 'Withdrawn'
    : credential.expired ? 'Expired' : 'In use'
  const colour = !credential.active ? T.textSecondary
    : credential.expired ? T.error : T.success

  return (
    <tr>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{credential.label}</div>
        <div style={{ ...helpStyle, margin: 0 }}>
          {credential.key_algorithm}
          {credential.allowed_profiles.length > 0
            && ` · ${credential.allowed_profiles.join(', ')} only`}
        </div>
      </td>
      <td style={tdStyle}>
        <div>{credential.subject_dn}</div>
        <div style={{ ...helpStyle, margin: 0 }}>
          issued by {credential.issuer_dn}
        </div>
      </td>
      <td style={tdStyle}>{formatDate(credential.not_after)}</td>
      <td style={{ ...tdStyle, color: colour }}>
        {state}
        {health && (
          <div style={{ ...helpStyle, margin: 0,
                        color: health.ok ? T.success : T.error }}>
            {health.detail}
          </div>
        )}
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={btnStyle} disabled={checking}
                  onClick={() => void check()}>
            {checking ? 'Checking…' : 'Check'}
          </button>
          {credential.active && (
            <button type="button" style={btnStyle}
                    onClick={() => void withdraw()}>
              Withdraw
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

// ── timestamps ─────────────────────────────────────────────────────────────

/**
 * The time-stamping authority, which is a property of WHOSE certificate is in
 * use rather than of the install.
 *
 * India's IT Act regime requires the timestamp come from the issuing CA's own
 * authority, not a public one — so it cannot be a global setting, and it is
 * read off the repository's own signature block.
 *
 * A regime that REQUIRES a timestamp and has none configured refuses to sign
 * rather than signing without one and reporting a level it did not reach. That
 * is the sentence here, because the alternative — a document claiming B-LT
 * that carries plain B-B — is exactly the kind of quiet overstatement this
 * whole feature is careful about.
 */
function TimestampPanel({ repository, patch, needed }: {
  repository: Record<string, any>
  patch: (path: string[], value: unknown) => void
  needed: boolean
}) {
  const value = String(repository?.signatures?.timestamp_url || '')
  return (
    <section style={panelStyle}>
      <h3 style={groupTitleStyle}>Time-stamping authority</h3>
      <p style={helpStyle}>
        An RFC 3161 endpoint that proves <em>when</em> a signature was applied,
        independently of this server’s clock. India’s IT Act regime requires it
        come from the issuing CA’s own authority rather than a public one,
        which is why it belongs to the repository and not to the install.
      </p>
      <label style={labelStyle}>
        Endpoint URL
        <input style={inputStyle} value={value}
               placeholder="https://timestamp.example-ca.com/tsa"
               onChange={(e) => patch(
                 ['repository', 'signatures', 'timestamp_url'],
                 e.target.value)} />
      </label>
      {needed && !value && (
        <div style={warnStyle}>
          A document type here declares a regime that requires a timestamp, and
          none is configured. Signing those documents will be refused with that
          reason — rather than signed without one and reported at a level they
          did not reach.
        </div>
      )}
    </section>
  )
}

function formatDate(value?: string): string {
  if (!value) return '—'
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString()
  } catch {
    return String(value)
  }
}

// ── styles ─────────────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = dmsCardStyle
const btnStyle: React.CSSProperties = dmsBtnStyle
const labelStyle: React.CSSProperties = dmsLabelStyle
const inputStyle: React.CSSProperties = { ...dmsInputStyle, width: '100%' }

const groupTitleStyle: React.CSSProperties = {
  margin: '0 0 6px', fontSize: TYPE.heading, fontWeight: 600,
}

const helpStyle: React.CSSProperties = {
  margin: '0 0 8px', fontSize: TYPE.small, color: T.textSecondary, lineHeight: 1.5,
}

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: TYPE.small,
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${T.border}`,
  fontWeight: 600, color: T.textSecondary,
}

const tdStyle: React.CSSProperties = {
  padding: '8px', borderBottom: `1px solid ${T.border}`, verticalAlign: 'top',
}

const checkLabelStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: TYPE.small, cursor: 'pointer',
}

const slotRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  padding: '6px 8px', borderRadius: RADIUS.sm,
  background: T.surface2,
}

const noteStyle: React.CSSProperties = {
  padding: '10px 12px', borderRadius: RADIUS.sm, fontSize: TYPE.small,
  lineHeight: 1.6, background: T.surface2, color: T.text,
}

const warnStyle: React.CSSProperties = {
  padding: '10px 12px', borderRadius: RADIUS.sm, fontSize: TYPE.small,
  lineHeight: 1.6, margin: '8px 0',
  background: T.warningWeak, color: T.warning,
}

const okStyle: React.CSSProperties = {
  padding: '10px 12px', borderRadius: RADIUS.sm, fontSize: TYPE.small,
  lineHeight: 1.6,
  background: T.successWeak, color: T.success,
}

export default SignaturesTab
